import { motion, type MotionValue } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CosmicBackground } from '../kickoff/KickoffPage';
import { QtplanLogo } from '../components/QtplanLogo';

export function LandingHero({
  heroRef,
  heroContentY,
  heroContentOpacity,
  heroVisible,
  goToBoards,
  goToCanvas,
  scrollToDemo,
}: {
  heroRef: React.RefObject<HTMLElement | null>;
  heroContentY: MotionValue<number>;
  heroContentOpacity: MotionValue<number>;
  heroVisible: boolean;
  goToBoards: () => void;
  goToCanvas: () => void;
  scrollToDemo: () => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="landing-hero" ref={heroRef}>
      <CosmicBackground style={{ position: 'absolute', inset: 0, zIndex: 0 }} active={heroVisible} />
      <div className="landing-hero-overlay" />

      <motion.div
        className="landing-hero-content"
        style={{ y: heroContentY, opacity: heroContentOpacity }}
      >
        <motion.div
          className="landing-hero-logo"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <QtplanLogo size={64} className="text-white" />
          <span className="landing-hero-logotext">qtplan</span>
        </motion.div>

        <motion.h1
          className="landing-hero-headline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {t('landing.heroHeadline').split('\n').map((line, i, arr) => (
            <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
          ))}
        </motion.h1>

        <motion.p
          className="landing-hero-sub"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {t('landing.heroSub')}
        </motion.p>

        <motion.div
          className="landing-hero-actions"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <button className="landing-btn-primary" onClick={goToCanvas}>
            {t('landing.createCanvas')}
          </button>
          <button className="landing-btn-primary" onClick={goToBoards}>
            {t('landing.createBoard')}
          </button>
          <button className="landing-btn-ghost" onClick={scrollToDemo}>
            {t('landing.playground')}
            <ArrowRight size={16} />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        className="landing-scroll-hint"
        aria-label="Scroll down"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        onClick={scrollToDemo}
      >
        <ChevronDown size={22} />
      </motion.button>
    </section>
  );
}
